import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import LinearProgress from '@mui/material/LinearProgress';
import { Typography } from '@qlub-dev/qlub-kit';
import { LoadingBubbles, OnlyQlubText, QRLogoSpinner } from '../SVG';
import { SplashConfigEnum } from '../../repository/vendor';
import { ISplashConfig } from './utils';
import { useTranslation } from '../../hook/translations';
import { shouldHideLogo } from '../../context/config';
import { useQlubRouter } from '../../hook/router';

import styles from './index.module.scss';

const themeConfig = {
    [SplashConfigEnum.Purple]: {
        background: 'var(--iris)',
        bubbleColor: '#FFF',
        textColor: '#FFF',
        footerColor: '#FFFFFFB2',
        logoColor: '#FFF',
        progressBarBGColor: 'rgba(255, 255, 255, 0.2)',
        progressColor: '#FFF',
    },
    [SplashConfigEnum.White]: {
        background: '#FFF',
        bubbleColor: '#7D00D4',
        textColor: 'var(--iris)',
        footerColor: 'var(--iris)',
        logoColor: '#7D00D4',
        progressBarBGColor: '#7d00d4, #5b009a, #40006c',
        progressColor: 'linear-gradient(225deg, #7d00d4 0%, #5b009a 63.83%, #40006c 100%)',
    },
    [SplashConfigEnum.Black]: {
        background: '#000',
        bubbleColor: '#FFF',
        textColor: '#FFF',
        footerColor: '#FFFFFFB2',
        logoColor: '#FFF',
        progressBarBGColor: 'rgba(255, 255, 255, 0.2)',
        progressColor: '#FFF',
    },
};

interface IProps {
    config: ISplashConfig;
    hideIcon?: boolean;
    smoothFinish?: boolean;
    stopProgress?: boolean;
}

export default function Splash({ config, hideIcon, smoothFinish, stopProgress }: IProps) {
    const { t } = useTranslation('common');
    const { query } = useQlubRouter();
    const [progress, setProgress] = useState(0);

    const hideLogo = useMemo(() => {
        return shouldHideLogo(query.hideLogo, hideIcon);
    }, [hideIcon, query]);

    useEffect(() => {
        if (stopProgress) {
            return;
        }

        const timer = setInterval(() => {
            setProgress((oldProgress) => {
                if (oldProgress >= 100) {
                    clearInterval(timer);

                    return 100;
                }

                const diff = Math.random() * 10;

                return Math.min(oldProgress + diff, 100);
            });
        }, 500);

        return () => {
            clearInterval(timer);
        };
    }, [smoothFinish]);

    useEffect(() => {
        if (smoothFinish && progress < 100) {
            setProgress(90);
        }
    }, [smoothFinish]);

    const theme = useMemo(() => {
        return themeConfig[config.theme];
    }, [config]);

    return (
        <div className={styles.splash}>
            <div
                className={styles.container}
                style={{
                    background: theme.background,
                }}
            >
                <div className={styles.loadingWrapper}>
                    <LoadingBubbles color={theme.bubbleColor} className={styles.bubbles} />
                    <LoadingBubbles
                        color={theme.bubbleColor}
                        className={classNames(styles.bubbles, styles.secondaryBubbles)}
                    />

                    {!hideLogo && (
                        <div className={styles.logoWrapper}>
                            <OnlyQlubText height={64} color={theme.logoColor} />
                            <QRLogoSpinner height={32} color={theme.logoColor} className={styles.spinner} />
                        </div>
                    )}

                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        className={styles.linearProgress}
                        sx={{
                            backgroundColor: theme.progressBarBGColor,
                            '& .MuiLinearProgress-bar': {
                                borderRadius: '10px',
                                backgroundColor: theme.progressColor,
                            },
                        }}
                    />

                    <Typography
                        component="h1"
                        className={styles.loadingTitle}
                        sx={{
                            color: theme.textColor!,
                        }}
                    >
                        {config.title}
                    </Typography>

                    <Typography
                        component="h1"
                        className={styles.loadingDescription}
                        sx={{
                            color: theme.textColor,
                        }}
                    >
                        {config.desc}
                    </Typography>

                    <Typography
                        className={styles.loadingFooter}
                        component="h1"
                        sx={{
                            color: theme.footerColor,
                        }}
                    >
                        {t('Preparing your bill in seconds')}
                    </Typography>
                </div>
            </div>
        </div>
    );
}
